import { mapTurnoverRequirement } from '../api-minigame-turnover/turnover-requirement.mappers';
import type {
  DailyCheckinBoard,
  DailyCheckinClaimResult,
  DailyCheckinPopupTheme,
  DailyCheckinReward,
  DailyCheckinRewardStatus,
} from '~/types/daily-checkin';
import { resolveCloudflareImage } from '~/utils/apostatudo/cloudflare-image';

// O check-in responde em snake_case e PLANO (`period_type`/`starts_on`/
// `ends_on` na raiz). Estes mappers traduzem a caixa, normalizam (coerção de
// tipo e defaults pra campo nulo) e dobram as três chaves do período no objeto
// que o front consulta pra saber se há jogo no ar.
//
// O contrato nasceu em camelCase e virou snake_case na revisão do ENG-425 — não
// sobrou nenhum campo camelCase na API. Ler só o nome novo é uma aposta
// consciente: o front tem que subir DEPOIS do core, senão a trilha inteira mapeia
// pra vazio e o modal mostra "sem período" com o jogo no ar.

const STATUSES: DailyCheckinRewardStatus[] = ['claimed', 'open', 'missed'];

// Status desconhecido cai em 'open': é o estado neutro: sem check, sem X e sem
// virar botão (só o primeiro da fila vira). Um valor novo no core degrada pra
// "degrau que ainda vem" em vez de sumir da trilha.
function mapStatus(value: unknown): DailyCheckinRewardStatus {
  return STATUSES.includes(value as DailyCheckinRewardStatus)
    ? (value as DailyCheckinRewardStatus)
    : 'open';
}

// Destino do botão USAR (`cta_url`). Devolve um PEDAÇO de objeto pra ser
// espalhado no resultado, e não um valor: é o que preserva o tri-estado do
// contrato (ver DailyCheckinReward.ctaUrl). Chave ausente no JSON → chave
// ausente no domínio, e quem decide o fallback é a tela; chave presente com
// `null` → string vazia, que é o core dizendo "este prêmio não tem destino".
//
// Escrever `ctaUrl: undefined` aqui pareceria igual e não é: no resgate, a
// store aplica o resultado por cima do degrau do board com `Object.assign`, e
// uma chave presente valendo `undefined` sobrescreve o que o board trouxe.
function mapCtaUrl(json: any): { ctaUrl?: string } {
  if (!json || !('cta_url' in json)) return {};

  return {
    ctaUrl: typeof json.cta_url === 'string' ? json.cta_url.trim() : '',
  };
}

// A arte do prêmio vem do Cloudflare Images, que exige sufixo de variant — sem
// ele a URL 404. O core manda a URL crua, então quem completa é o adapter, no
// mesmo lugar (e com a mesma função) que os mappers da roletinha: a variant é
// detalhe de transporte, não pode vazar pro componente. `resolveCloudflareImage`
// tira a variant que porventura já tenha vindo antes de aplicar `/public`, então
// serve tanto pra URL crua quanto pra uma que já chegue pronta.
export function mapReward(json: any): DailyCheckinReward {
  return {
    position: Number(json.position),
    title: json.title ?? '',
    cardLabel: json.card_label ?? null,
    description: json.description ?? null,
    imageUrl: resolveCloudflareImage(json.image_url),
    ...mapCtaUrl(json),
    isFeatured: !!json.is_featured,
    status: mapStatus(json.status),
    theme: mapPopupTheme(json),
    // Prêmio não revelado. O core não manda nome, arte nem cor neste degrau — os
    // campos acima já caíram nos vazios por isso, e é esta chave que separa
    // "escondido de propósito" de "degrau sem prêmio cadastrado".
    //
    // Default `false`: silêncio é trilha aberta, que é o que ela sempre foi.
    isHidden: json.is_hidden === true,
  };
}

// O `is_eligible` da rota é IGNORADO de propósito (ENG-787): elegibilidade é o
// nível do jogador contra o mínimo da gamificação, e isso só existe no
// `/players/me`. Ler o campo daqui era um SEGUNDO veredito, sobre outro fato
// ("já fez uma aposta real"), competindo com o do nível. O core remove o campo
// no ENG-786; até lá ele chega no payload e não é mapeado.
// O visual que o popup tinha ANTES de virar configurável, campo a campo. É o
// fallback de CADA chave, e não um "tema padrão" inteiro: um core que mande só
// metade do bloco não pode apagar a outra metade.
//
// Ao contrário do resto do arquivo, aqui o front PODE subir antes do core sem
// estrago: sem `popup_theme` o card continua exatamente como está hoje. Quem
// muda a cor no backoffice é que precisa do core no ar. O mesmo fallback cobre o
// degrau que ainda não tem prêmio escolhido, onde o core manda `null`.
const THEME_FALLBACK: DailyCheckinPopupTheme = {
  availableBackgroundFrom: '#060642',
  availableBackgroundTo: '#0F0FA8',
  claimedBackgroundFrom: '#003C07',
  claimedBackgroundTo: '#009D12',
  buttonColor: '#1414DD',
  buttonLabel: 'RESGATAR',
  descriptionColor: '#00FF1D',
  descriptionText: 'Resgate concluído com sucesso!',
};

// String vazia conta como ausente: um campo apagado no core não pode deixar o
// botão sem legenda nem o card sem fundo.
function themeValue(value: unknown, fallback: string): string {
  return typeof value === 'string' && value.trim() !== ''
    ? value.trim()
    : fallback;
}

export function mapPopupTheme(json: any): DailyCheckinPopupTheme {
  const theme = json?.popup_theme ?? {};

  return {
    availableBackgroundFrom: themeValue(
      theme.available_background_from,
      THEME_FALLBACK.availableBackgroundFrom,
    ),
    availableBackgroundTo: themeValue(
      theme.available_background_to,
      THEME_FALLBACK.availableBackgroundTo,
    ),
    claimedBackgroundFrom: themeValue(
      theme.claimed_background_from,
      THEME_FALLBACK.claimedBackgroundFrom,
    ),
    claimedBackgroundTo: themeValue(
      theme.claimed_background_to,
      THEME_FALLBACK.claimedBackgroundTo,
    ),
    buttonColor: themeValue(theme.button_color, THEME_FALLBACK.buttonColor),
    buttonLabel: themeValue(theme.button_label, THEME_FALLBACK.buttonLabel),
    descriptionColor: themeValue(
      theme.description_color,
      THEME_FALLBACK.descriptionColor,
    ),
    descriptionText: themeValue(
      theme.description_text,
      THEME_FALLBACK.descriptionText,
    ),
  };
}

export function mapBoard(json: any): DailyCheckinBoard {
  return {
    // `!== false` e NÃO `!!`, de propósito. O core promete `is_active` sempre
    // presente e nunca nulo (ENG-792), mas o default de quem lê é o que decide o
    // estrago quando essa promessa não vale: num payload sem o campo — front no
    // ar antes do core — o `!!` daria `false` e TODO jogador veria "jornada
    // finalizada" com a promoção rodando. Aqui o silêncio é lido como ligado, e
    // só o `false` explícito desliga: o erro possível vira mostrar o check-in
    // pra quem não devia, que o próprio claim recusa com `check_in_disabled`.
    isActive: json.is_active !== false,
    // Sem período publicado o core devolve 200 com `period_type: null` e lista
    // vazia — é o sinal pro front esconder o jogo, não um erro.
    period: json.period_type
      ? {
          type: json.period_type,
          startsOn: json.starts_on,
          endsOn: json.ends_on,
          // `terms` também vem na raiz e entra no período pelo mesmo motivo que
          // as datas: é o regulamento DAQUELE ciclo. Sem período publicado o
          // campo não tem onde existir, e o modal cai na copy de fallback.
          terms: json.terms ?? null,
        }
      : null,
    canClaimToday: !!json.can_claim_today,
    nextClaimAvailableAt: json.next_claim_available_at ?? null,
    claimedCount: Number(json.claimed_count ?? 0),
    remainingCount: Number(json.remaining_count ?? 0),
    missedDays: Number(json.missed_days ?? 0),
    rewards: Array.isArray(json.rewards) ? json.rewards.map(mapReward) : [],
    // ENG-820. Sempre um objeto: um payload sem o bloco vira "não se aplica",
    // nunca um bloqueio inventado (ver mapTurnoverRequirement).
    turnoverRequirement: mapTurnoverRequirement(json.turnover_requirement),
    // `hide_upcoming_prizes` fica de fora, como `is_eligible`: quem esconde é o
    // core, no `is_hidden` de cada degrau (ver o tipo do board).
  };
}

// O resgate devolve o prêmio solto na raiz, sem status nem contadores — e sem
// `card_label`, que só existe no board.
export function mapClaimResult(json: any): DailyCheckinClaimResult {
  return {
    position: Number(json.position),
    title: json.title ?? '',
    description: json.description ?? null,
    imageUrl: resolveCloudflareImage(json.image_url),
    ...mapCtaUrl(json),
    isFeatured: !!json.is_featured,
  };
}
